import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import {
  Bell,
  Instagram,
  Twitter,
  Youtube,
  Radio
} from 'lucide-react';

const API_URL = '';  // Use relative URLs with proxy

const ReviewerPublicPage = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const [reviewer, setReviewer] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initial fetch
  useEffect(() => {
    const fetchReviewer = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/public/reviewer/${username}`);
        setReviewer(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching reviewer:', error);
        setLoading(false);
      }
    };
    
    fetchReviewer();
  }, [username]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-2xl text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!reviewer) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Reviewer Not Found</h2>
          <p className="text-gray-600 mb-4">We couldn&apos;t find this reviewer.</p>
          <Button onClick={() => navigate('/reviewers')}>Browse Reviewers</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#FFCC00] to-[#FFD700] py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Avatar className="w-32 h-32 mx-auto mb-4 border-4 border-white shadow-lg">
            <AvatarImage src={reviewer.avatar} alt={reviewer.name} />
            <AvatarFallback className="text-4xl bg-black text-[#FFCC00]">
              {reviewer.name?.charAt(0) || 'R'}
            </AvatarFallback>
          </Avatar>
          <h1 className="text-4xl font-bold text-black mb-2">{reviewer.name}</h1>
          <p className="text-xl text-gray-800 mb-4">@{reviewer.username}</p>
          {reviewer.is_live && (
            <Badge className="bg-red-500 text-white hover:bg-red-500">
              <Radio className="h-4 w-4 mr-1 animate-pulse" />
              LIVE NOW
            </Badge>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-3 gap-8">
          {/* Left Column - Profile Info */}
          <div className="md:col-span-1">
            {/* Genres */}
            {reviewer.genres && reviewer.genres.length > 0 && (
              <div className="mb-6">
                <h3 className="font-bold text-gray-800 mb-3">Genres</h3>
                <div className="flex flex-wrap gap-2">
                  {reviewer.genres.map((genre, idx) => (
                    <Badge key={idx} variant="outline" className="border-[#FFCC00] text-gray-800">
                      {genre}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Bio */}
            {reviewer.bio && (
              <div className="mb-6 text-left">
                <h3 className="font-bold text-gray-800 mb-2">About</h3>
                <p className="text-gray-700">{reviewer.bio}</p>
              </div>
            )}

            {/* Stats - UPDATED to use reviewCount and rating object */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-3xl font-bold text-black">
                  {reviewer.totalReviews || 0}
                </div>
                <div className="text-sm text-gray-600">Reviews</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-3xl font-bold text-black">
                  {reviewer.rating?.average 
                    ? `${reviewer.rating.average}⭐` 
                    : `${reviewer.average_rating || 0}⭐`}
                </div>
                <div className="text-sm text-gray-600">
                  Rating
                  {reviewer.rating?.count > 0 && (
                    <span className="text-xs text-gray-500 block mt-1">({reviewer.rating.count} ratings)</span>
                  )}
                </div>
              </div>
            </div>

            {/* Notify Button */}
            <Button
              className="w-full bg-[#FFCC00] hover:bg-[#FFCC00]/90 text-black font-bold"
              size="lg"
            >
              <Bell className="mr-2 h-5 w-5" />
              Notify Me When Live
            </Button>

            {/* Social Links */}
            {(reviewer.instagram || reviewer.twitter || reviewer.youtube) && (
              <div className="mt-6">
                <h3 className="font-bold text-gray-800 mb-3">Connect</h3>
                <div className="flex gap-3">
                  {reviewer.instagram && (
                    <a
                      href={reviewer.instagram.startsWith('http') ? reviewer.instagram : `https://instagram.com/${reviewer.instagram}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-black rounded-full flex items-center justify-center hover:bg-gray-800 transition"
                    >
                      <Instagram className="h-5 w-5 text-white" />
                    </a>
                  )}
                  {reviewer.twitter && (
                    <a
                      href={reviewer.twitter.startsWith('http') ? reviewer.twitter : `https://twitter.com/${reviewer.twitter}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-black rounded-full flex items-center justify-center hover:bg-gray-800 transition"
                    >
                      <Twitter className="h-5 w-5 text-white" />
                    </a>
                  )}
                  {reviewer.youtube && (
                    <a
                      href={reviewer.youtube.startsWith('http') ? reviewer.youtube : `https://youtube.com/@${reviewer.youtube}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-black rounded-full flex items-center justify-center hover:bg-gray-800 transition"
                    >
                      <Youtube className="h-5 w-5 text-white" />
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Recent Sessions / Activity */}
          <div className="md:col-span-2">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Recent Activity</h2>
            <Card className="p-6">
              {reviewer.is_live ? (
                <div className="text-center py-8">
                  <Radio className="h-16 w-16 text-red-500 mx-auto mb-4 animate-pulse" />
                  <h3 className="text-xl font-bold mb-2">Currently Live!</h3>
                  <p className="text-gray-600 mb-4">Join the session now and get your music reviewed.</p>
                  <Button
                    onClick={() => navigate(`/upload?reviewer=${reviewer.id}`)}
                    className="bg-black hover:bg-gray-800 text-white"
                    size="lg"
                  >
                    <Music className="mr-2 h-5 w-5" />
                    Submit Your Track
                  </Button>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Music className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-2">Not Live Right Now</h3>
                  <p className="text-gray-600 mb-4">
                    {reviewer.name} isn&apos;t streaming at the moment. Get notified when they're available!
                  </p>
                  <Button
                    className="bg-[#FFCC00] hover:bg-[#FFCC00]/90 text-black font-bold"
                  >
                    <Bell className="mr-2 h-5 w-5" />
                    Notify Me
                  </Button>
                </div>
              )}
            </Card>

            {/* Reviews/Testimonials could go here in the future */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewerPublicPage;
